/**
 * AppointmentsMobile.js
 * Layout mobile dedicado para a tela de Agendamentos.
 * Recebe toda a lógica de estado do Appointments.js pai via props.
 */

import React, { useRef, useEffect, useState } from 'react';
import styles from './AppointmentsMobile.module.css';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import CalendarViewDayOutlinedIcon from '@mui/icons-material/CalendarViewDayOutlined';
import CalendarViewWeekOutlinedIcon from '@mui/icons-material/CalendarViewWeekOutlined';
import CalendarViewMonthOutlinedIcon from '@mui/icons-material/CalendarViewMonthOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';

import AddIcon from '@mui/icons-material/Add';
import CalendarMobile from './Calendar/CalendarMobile';
import MonthlyCalendar from './MonthlyCalendar/MonthlyCalendar';

// ─── Função auxiliar: label de data para o mobile ────────────────────────────
function getMobileDateLabel(viewMode, viewDaysCount, getDaysForCurrentView, monthlyCurrentDate) {
  if (viewMode === 'monthly') {
    return monthlyCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  const days = getDaysForCurrentView();
  if (!days || days.length === 0) return '';

  if (viewMode === 'daily' || viewMode === 'professionals') {
    const day = days[0];
    return day.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // weekly
  const first = days[0];
  const last = days[days.length - 1];
  const firstDay = first.getDate();
  const lastDay = last.getDate();
  const month = last.toLocaleDateString('pt-BR', { month: 'short' });
  const year = last.getFullYear();
  return `${firstDay}–${lastDay} ${month} ${year}`;
}

// ─── Ícone de filtro (SVG inline) ─────────────────────────────────────────────
const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="11" y1="18" x2="13" y2="18" />
  </svg>
);

// ─── Ícone chevron esquerdo ───────────────────────────────────────────────────
const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

// ─── Ícone chevron direito ────────────────────────────────────────────────────
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ─── Componente Principal ─────────────────────────────────────────────────────
const AppointmentsMobile = ({
  // Estado do calendário
  viewMode,
  viewDaysCount,
  page,

  // Dados
  filteredAppointments,

  // Funções de data
  getDaysForCurrentView,

  // Handlers de navegação
  onPrev,
  onNext,
  onViewModeChange,
  onToday,
  onDayClickMonthly,

  // Handlers de popup
  onAppointmentClick,
  onEmptySlotClick,

  // Filtros
  isSidebarOpen,
  onToggleSidebar,
  activeFiltersCount,

  // Config
  companyConfig,
  isUpdating,

  // Monthly — lista do mês inteiro (mesma que o desktop usa), não a do dia/semana
  monthlyAppointments = [],
  monthlyCurrentDate,
  onMonthlyPrev,
  onMonthlyNext,

  // Intervalo de slot (em minutos) — dinâmico via CompanyConfig.TimeInterval
  slotIntervalMinutes,

  // Handler para criar agendamento via picker
  onCreateAppointment,

  // Agendamento recém-salvo: o cartão ganha destaque por alguns segundos
  highlightedAppointment = null,

  // Modo "Profissionais": colunas [{ id, label, room? }]; `room` é a sala do dia (chip só leitura)
  professionalColumns = null,

  // Horário próprio de cada agenda e a agenda única do filtro (listras fora do horário)
  resourceSchedules = null,
  focusedResourceId = null,
  // Faixa da grade (calendarHourRange): empresa + horário próprio das agendas
  calendarRange = null,
  // Lista bruta do período (sem filtro de situação): decide quais agendas estão sem nada no dia
  allAppointments = null,
  // "Localizar profissional" (modo Profissionais com muitas colunas): abre o menu de agendas;
  // null esconde o botão
  onGoToResource = null,
  // Coluna escolhida no menu: a cada nonce novo a grade rola até ela e a destaca
  focusColumnId = null,
  focusColumnNonce = 0,
}) => {
  // ── Refs para medir altura dos elementos acima do calendário ──
  const headerRef = useRef(null);
  const controlsRef = useRef(null);

  // ── Altura calculada dinamicamente para a área do calendário ──
  const [calendarHeight, setCalendarHeight] = useState('60vh'); // fallback inicial

  useEffect(() => {
    const updateHeight = () => {
      const appHeaderHeight = 50; // Header.css fixa 50px
      const pageHeaderHeight = headerRef.current?.offsetHeight || 60;
      const controlsHeight = controlsRef.current?.offsetHeight || 44;
      const totalAbove = appHeaderHeight + pageHeaderHeight + controlsHeight;
      const available = window.innerHeight - totalAbove;
      setCalendarHeight(`${Math.max(available, 200)}px`);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const dateLabel = getMobileDateLabel(
    viewMode,
    viewDaysCount,
    getDaysForCurrentView,
    monthlyCurrentDate
  );

  const handlePrev = () => {
    if (viewMode === 'monthly') {
      onMonthlyPrev();
    } else {
      onPrev();
    }
  };

  const handleNext = () => {
    if (viewMode === 'monthly') {
      onMonthlyNext();
    } else {
      onNext();
    }
  };

  return (
    <div className={styles.mobileContainer}>

      {/* ── Header compacto ── */}
      <div ref={headerRef} className={styles.mobilePageHeader}>
        <div className={styles.mobilePageHeaderLeft}>
          <div className={styles.pageIcon}>
            <EventNoteOutlinedIcon sx={{ fontSize: 17 }} />
          </div>
          <h1 className={styles.mobilePageTitle}>Agendamentos</h1>
        </div>
        <div className={styles.mobilePageHeaderRight}>
          <button
            type="button"
            className={[
              styles.mobileFilterBtn,
              isSidebarOpen ? styles.mobileFilterBtnActive : '',
            ].filter(Boolean).join(' ')}
            onClick={onToggleSidebar}
            aria-label="Filtros"
          >
            <FilterIcon />
            {activeFiltersCount > 0 && (
              <span className={styles.mobileFilterBadge}>{activeFiltersCount}</span>
            )}
          </button>
          {onCreateAppointment && (
            <button
              type="button"
              className={styles.mobileCreateBtn}
              onClick={onCreateAppointment}
              aria-label="Criar agendamento"
              data-tour="agenda-create"
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      </div>

      {/* ── Barra de controles ── */}
      <div ref={controlsRef} className={styles.mobileControlsBar}>
        {/* Linha 1: seletor de view + seletor de densidade + botão Hoje */}
        <div className={styles.mobileControlsRow1}>
          <div className={styles.mobileViewSelector}>
            <button
              type="button"
              className={[
                styles.mobileViewBtn,
                viewMode === 'daily' ? styles.mobileViewBtnActive : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onViewModeChange('daily')}
            >
              <CalendarViewDayOutlinedIcon sx={{ fontSize: 14 }} />
              Dia
            </button>
            <button
              type="button"
              className={[
                styles.mobileViewBtn,
                viewMode === 'weekly' ? styles.mobileViewBtnActive : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onViewModeChange('weekly')}
            >
              <CalendarViewWeekOutlinedIcon sx={{ fontSize: 14 }} />
              Semana
            </button>
            <button
              type="button"
              className={[
                styles.mobileViewBtn,
                viewMode === 'monthly' ? styles.mobileViewBtnActive : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onViewModeChange('monthly')}
            >
              <CalendarViewMonthOutlinedIcon sx={{ fontSize: 14 }} />
              Mês
            </button>
            <button
              type="button"
              className={[
                styles.mobileViewBtn,
                viewMode === 'professionals' ? styles.mobileViewBtnActive : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onViewModeChange('professionals')}
            >
              <ViewWeekOutlinedIcon sx={{ fontSize: 14 }} />
              Profissionais
            </button>
          </div>

          <button type="button" className={styles.mobileTodayBtn} onClick={onToday}>
            <TodayOutlinedIcon sx={{ fontSize: 14 }} />
            Hoje
          </button>
        </div>

        {/* Linha 2: navegação de data */}
        <div className={styles.mobileControlsRow2}>
          <div className={styles.mobileDateNav}>
            <button
              type="button"
              className={styles.mobileDateNavBtn}
              onClick={handlePrev}
              aria-label="Período anterior"
            >
              <ChevronLeft />
            </button>
            <span className={styles.mobileDateLabel}>{dateLabel}</span>
            <button
              type="button"
              className={styles.mobileDateNavBtn}
              onClick={handleNext}
              aria-label="Próximo período"
            >
              <ChevronRight />
            </button>
          </div>
          {/* "Localizar profissional": com muitas colunas, rola direto até o profissional */}
          {onGoToResource && (
            <button
              type="button"
              className={styles.mobileGoToResourceBtn}
              onClick={onGoToResource}
              title="Encontrar a coluna de um profissional e rolar a tela até ela"
            >
              <PersonSearchOutlinedIcon sx={{ fontSize: 14 }} />
              Localizar profissional
            </button>
          )}
        </div>
      </div>

      {/* ── Área do calendário — altura calculada; scroll delegado ao filho ──
          contain: inline-size — a largura da grade (semana = 536px) não pode
          subir pela árvore: .app-content é item de um flex row com min-width
          automático e esticava a página inteira (cabeçalho, "+", Filtros e Hoje
          saíam da tela). Com a contenção, a grade rola dentro do próprio wrapper. */}
      <div style={{ height: calendarHeight, overflow: viewMode === 'monthly' ? 'auto' : 'hidden', position: 'relative', contain: 'inline-size' }}>
        {viewMode === 'monthly' ? (
          <MonthlyCalendar
            currentDate={monthlyCurrentDate}
            appointments={monthlyAppointments}
            onDayClick={(date) => {
              // Navega para a view diária do dia clicado, exatamente como no desktop
              if (onDayClickMonthly) {
                onDayClickMonthly(date);
              } else {
                onViewModeChange('daily');
              }
            }}
          />
        ) : (
          <CalendarMobile
            days={getDaysForCurrentView()}
            appointments={filteredAppointments}
            onAppointmentClick={onAppointmentClick}
            onEmptySlotClick={onEmptySlotClick}
            startHour={calendarRange?.startHour ?? companyConfig?.AvailableTime?.[0] ?? 7}
            endHour={calendarRange?.endHour ?? companyConfig?.AvailableTime?.[1] ?? 20}
            containerHeight={parseInt(calendarHeight, 10)}
            availableDaysOfWeek={companyConfig?.AvailableDaysOfWeek ?? null}
            blockedTimes={companyConfig?.BlockedTimes ?? []}
            slotIntervalMinutes={slotIntervalMinutes}
            highlightedAppointment={highlightedAppointment}
            resourceColumns={viewMode === 'professionals' ? professionalColumns : null}
            resourceSchedules={resourceSchedules}
            focusedResourceId={focusedResourceId}
            companyHours={calendarRange?.widened ? calendarRange.companyHours : null}
            allAppointments={allAppointments}
            focusColumnId={focusColumnId}
            focusColumnNonce={focusColumnNonce}
          />
        )}
      </div>

    </div>
  );
};

export default AppointmentsMobile;
